import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal } from "lucide-react";

const SIZES = ["PP","P","M","G","GG","XGG","46","48","50","52","54"];
const COLORS = ["Preto","Branco","Vermelho","Bege","Rosa","Azul marinho","Cinza","Verde militar"];

function Filters({ filters, setFilters }) {
  return (
    <div className="space-y-6 text-sm">
      <div>
        <h4 className="font-semibold mb-2">Tamanho</h4>
        <div className="grid grid-cols-3 gap-2">
          {SIZES.map((s) => (
            <button
              key={s}
              onClick={() => setFilters((f) => ({ ...f, size: f.size === s ? undefined : s }))}
              className={`border rounded-md py-2 text-xs font-medium ${filters.size === s ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`}
            >{s}</button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-semibold mb-2">Cor</h4>
        <div className="flex flex-wrap gap-2">
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setFilters((f) => ({ ...f, color: f.color === c ? undefined : c }))}
              className={`border rounded-full px-3 py-1 text-xs ${filters.color === c ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`}
            >{c}</button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-semibold mb-2">Faixa de preço</h4>
        <div className="flex gap-2">
          <input type="number" placeholder="Min" value={filters.min_price || ""} onChange={(e) => setFilters((f) => ({ ...f, min_price: e.target.value || undefined }))} className="w-full border rounded-md px-3 py-2 text-sm" />
          <input type="number" placeholder="Max" value={filters.max_price || ""} onChange={(e) => setFilters((f) => ({ ...f, max_price: e.target.value || undefined }))} className="w-full border rounded-md px-3 py-2 text-sm" />
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!filters.on_sale} onChange={(e) => setFilters((f) => ({ ...f, on_sale: e.target.checked || undefined }))} />
        <span>Somente promoções</span>
      </label>
      <Button variant="outline" onClick={() => setFilters({})} className="w-full">Limpar filtros</Button>
    </div>
  );
}

export default function Category() {
  const { name } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState("relevance");

  useEffect(() => {
    setLoading(true);
    const params = { category: name, sort, ...filters };
    if (name === "Novidades") { delete params.category; params.is_new = true; }
    if (name === "Promoções") { delete params.category; params.on_sale = true; }
    api.get("/products", { params }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [name, filters, sort]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:py-10">
      <nav className="text-xs text-neutral-500 mb-4">
        <a href="/">Início</a> / <span className="text-neutral-900 font-medium">{name}</span>
      </nav>
      <div className="flex items-center justify-between mb-6 gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{name}</h1>
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="lg:hidden" data-testid="filters-btn">
                <SlidersHorizontal className="h-4 w-4 mr-1" /> Filtros
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80">
              <SheetHeader><SheetTitle>Filtros</SheetTitle></SheetHeader>
              <div className="mt-6"><Filters filters={filters} setFilters={setFilters} /></div>
            </SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-44" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Mais relevantes</SelectItem>
              <SelectItem value="newest">Novidades</SelectItem>
              <SelectItem value="price_asc">Menor preço</SelectItem>
              <SelectItem value="price_desc">Maior preço</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="hidden lg:block">
          <Filters filters={filters} setFilters={setFilters} />
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-neutral-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-neutral-500">Nenhum produto encontrado.</p>
              <Button variant="outline" className="mt-4" onClick={() => setFilters({})}>Limpar filtros</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
